import { Drawer, DrawerBody, DrawerContent, DrawerHeader, DrawerOverlay, Flex, Grid, GridItem, ModalFooter, Text, useToast } from '@chakra-ui/react'
import { useOrcamentoPedido } from '../../../../../../contexts/OrcamentoPedidoProvider'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { cotacaoFrete } from '../../../func/cotacaoFrete'
import { formatPrice } from '../../../../../../utils/formatPrice'
import { Button } from '../../../../../../components/Button'
import { Item } from '../CotacaoFrete/Item'
import { verificarCaixaAgrupada } from '../../../func/verificarCaixaAgrupada'
import { salvarCotacao } from '../../../func/salvarCotacao'
import { useParams } from 'react-router-dom'
import { ListItems } from '../CotacaoFrete/ListItems'
import { Caixa, Caixas } from '../CotacaoFrete/Caixas'
import { CodigoTransportadoraCliente } from './CodigoTransportadoraCliente'

export function CalcularTodosFretes({ isOpen, onClose }) {
  const { infoProposta, handleInformacoesFrete, informacoesFrete, itensPermitidos: itens } = useOrcamentoPedido()
  const queryClient = useQueryClient()
  const { numeroProposta: numProposta } = useParams()
  const transportadoraClienteRef = useRef(null)

  const toast = useToast({
    position: 'top-right',
    duration: 5000,
    isClosable: true,
  })

  const valorTotalItens = itens?.reduce((acc, item) => {
    return acc + parseFloat(item.original_valor_total_itens)
  }, 0)

  const totalPeso = itens?.reduce((acc, item) => {
    return acc + parseFloat(item.peso)
  }, 0)

  const qtdTotalCaixas = itens?.reduce((acc, item) => {
    return acc + parseFloat(item.quantidade_caixa)
  }, 0)

  const totalCalcKangu = itens?.reduce((acc, item) => {
    return acc + parseFloat(item.calc_kangu)
  }, 0)

  const calc = verificarCaixaAgrupada({
    calcKangu: totalCalcKangu,
    qtdItens: itens.length,
    qtdCaixas: qtdTotalCaixas,
  })

  const [selectedItem, setSelectedItem] = useState(null)

  function handleSelectItem(frete) {
    const infoFrete = {
      ...frete,
      valor_total_frete_itens: valorTotalItens + parseFloat(frete.original_valor_total_frete_30) + parseFloat(infoProposta?.vlr_subs),
    }
    handleInformacoesFrete(infoFrete)
    setSelectedItem(infoFrete)
  }

  const {
    data: transportadoras = {
      integradas: [],
      internas: [],
    },
    isLoading,
    mutateAsync,
    isError,
  } = useMutation({
    mutationFn: cotacaoFrete,

    onError: (error) => {
      if (error.response.status === 400) {
        toast({
          title: error['response']['data'].mensagem,
          status: 'error',
        })
        return
      }

      const errors = error['response']['data'].errors

      const errorsKeys = Object.keys(errors)

      errorsKeys.forEach((key) => {
        const error = errors[key]
        toast({
          title: error[0],
          status: 'error',
        })
      })
    },
  })

  const handleCalculateFrete = useCallback(async () => {
    let cubagens = []
    if (calc?.value) {
      cubagens = [
        {
          peso: totalPeso,
          altura: calc.altura,
          largura: calc.largura,
          comprimento: calc.comprimento,
          valor: valorTotalItens,
          quantidade_caixas: 1,
        },
      ]
    }

    if (!calc?.value) {
      cubagens = itens.map((item) => {
        return {
          peso: parseFloat(item.peso),
          altura: calc ? calc.altura : parseFloat(item.cubagem.dimensoes.altura),
          largura: calc ? calc.largura : parseFloat(item.cubagem.dimensoes.largura),
          comprimento: calc ? calc.comprimento : parseFloat(item.cubagem.dimensoes.comprimento),
          valor: item.original_valor_total_itens,
          quantidade_caixas: parseInt(item.cubagem.dimensoes.qtdcaixa),
        }
      })
    }

    const isMotoboy = !!calc?.value && calc?.value < 18.04

    const params = {
      cubagem: cubagens,
      numeroNota: infoProposta.num_proposta,
      cpfCnpj: infoProposta.dados_entrega.cnpj,
      cep: infoProposta.dados_entrega.cep,
      valorTotalItens: valorTotalItens,
      ie: infoProposta.dados_entrega.inscricao_estadual,
      qtdCaixas: calc?.value ? 1 : qtdTotalCaixas,
      motoboy: isMotoboy,
    }

    await mutateAsync(params)
  }, [infoProposta, mutateAsync, valorTotalItens, qtdTotalCaixas, calc])

  const { mutateAsync: salvarCotacaoFn, isLoading: salvarCotacaoFreteLoading } = useMutation({
    mutationFn: salvarCotacao,
    onSuccess: (data) => {
      onClose()
      toast({
        title: 'Cotação de frete salva com sucesso',
        status: 'success',
      })

      queryClient.setQueryData(['items', numProposta], (oldData) => {
        return {
          ...oldData,
          informacao_da_proposta: {
            ...oldData.informacao_da_proposta,
            transportadora: {
              nome_transportadora: data.nome_transportadora,
              prazo: data.prazo,
              frete_gratis: data.frete_gratis,
              frete_unificado: data.frete_unificado,
              valor_simulacao: data.valor_simulacao,
              valor_frete_30: data.valor_simulacao,
            },
          },
          itens: itens.map((item) => {
            return {
              ...item,
              transportadora: null,
            }
          }),
        }
      })
    },
    onError: (error) => {
      toast({
        title: 'Erro ao salvar cotação de frete',
        status: 'error',
      })
    },
  })

  async function handleCalcSave() {
    if (selectedItem?.nome_transportadora === 'Transportadora Cliente') {
      transportadoraClienteRef.current.openModal()
      return
    }

    await handleSave({ codigo_tc: null })
  }

  async function handleSave({ codigo_tc = null }) {
    const isAgrupada = calc?.value ? calc?.value : null

    const params = {
      agrupamento: isAgrupada,
      frete_real: selectedItem.original_valor_frete,
      valor_simulacao: selectedItem.original_valor_total_frete_30,
      numero_nota: infoProposta.num_proposta,
      nome_transportadora: selectedItem.nome_transportadora,
      prazo: selectedItem.prazo_entrega,
      peso: selectedItem.peso ? selectedItem.peso : 0,
      quantidade_caixa: qtdTotalCaixas,
      frete_gratis: selectedItem.gratis,
      tipo_servico: selectedItem.tipo_servico,
      calc_kangu: calc?.key,
      referencia: selectedItem.referencia,
      cnpj_transportadora: selectedItem.cnpj_transportadora,
      frete_unificado: true,
      codigo_tc: codigo_tc,
    }

    await salvarCotacaoFn(params)
  }

  useEffect(() => {
    if (isOpen) {
      handleCalculateFrete()
    }
  }, [isOpen, handleCalculateFrete])

  const caixasItens = useMemo(() => {
    const caixas = itens.map((item) => {
      const pesoTotal = parseFloat(item.cubagem.unitario) * parseInt(item.quantidade_caixa)

      return {
        caixa: item.cubagem.dimensoes.caixa,
        qtdCaixa: item.quantidade_caixa,
        peso: item.cubagem.unitario,
        totalPeso: parseFloat(item.peso),
        total: pesoTotal,
      }
    })

    const caixasAgrupadas = caixas.reduce((acc, item) => {
      const existItem = acc.find((caixa) => caixa.caixa === item.caixa)

      if (existItem) {
        existItem.qtdCaixa = parseInt(existItem.qtdCaixa) + parseInt(item.qtdCaixa)
        existItem.total = parseFloat(existItem.total) + parseFloat(item.total)
        existItem.totalPeso = parseFloat(existItem.totalPeso) + parseFloat(item.totalPeso)
        return acc
      }

      return [...acc, item]
    }, [])

    return caixasAgrupadas
  }, [itens])

  return (
    <>
      <Drawer placement="bottom" onClose={onClose} isOpen={isOpen}>
        <CodigoTransportadoraCliente ref={transportadoraClienteRef} onSave={handleSave} isLoading={salvarCotacaoFreteLoading} />
        <DrawerOverlay />
        <DrawerContent minH="100vh">
          <DrawerHeader bgColor="#378BBF" textAlign="center" color="#F5F5F5" textTransform="uppercase">
            Frete
          </DrawerHeader>
          {isLoading && (
            <DrawerBody display="flex" justifyContent="center" alignItems="center">
              <Text fontSize="20px" color="blue.500" fontWeight="bold" textAlign="center">
                Calculando frete, aguarde...
              </Text>
            </DrawerBody>
          )}

          {!isLoading && (
            <DrawerBody display="flex" flexDirection="column" gap={4}>
              <Flex flexDir="column" maxW="1316px" mx="auto" w="full" gap={4}>
                <ListItems list={itens} />
                {!calc?.value ? (
                  caixasItens?.map((item) => (
                    <Caixas
                      key={item.id}
                      calc={calc}
                      item={{
                        cubagem: {
                          dimensoes: {
                            caixa: item.caixa,
                            qtdcaixa: item.qtdCaixa,
                          },
                          unitario: parseFloat(item.peso)?.toFixed(2),
                          total: item.total,
                        },
                      }}
                    />
                  ))
                ) : (
                  <Caixa calc={calc} item={{ cubagem: { dimensoes: { caixa: 'Caixa Utilizada', pesocaixa: totalPeso, total: totalPeso } } }} />
                )}
              </Flex>

              <Grid gridTemplateColumns="repeat(2, 640px)" columnGap="35px" rowGap="23px" mx="auto" color="black" fontSize="15px" maxW="1316px">
                <GridItem py={1} display="flex" flexDirection="column" gap={4}>
                  {transportadoras?.integradas?.map((item, index) => (
                    <Item
                      key={item.id}
                      item={item}
                      itemSelected={selectedItem?.nome_transportadora === item.nome_transportadora}
                      handleSelectItem={handleSelectItem}
                    />
                  ))}

                  {transportadoras?.erro && (
                    <Text color="red.500" fontSize="15px" fontWeight="bold" textAlign="center" mt={10}>
                      {transportadoras?.erro}
                    </Text>
                  )}
                </GridItem>
                <GridItem py={1} display="flex" flexDirection="column" gap={4}>
                  {transportadoras?.internas?.map((item, index) => (
                    <Item
                      key={item.id}
                      item={item}
                      itemSelected={selectedItem?.nome_transportadora === item.nome_transportadora}
                      handleSelectItem={handleSelectItem}
                    />
                  ))}
                </GridItem>
              </Grid>

              {isError && (
                <DrawerBody display="flex" justifyContent="center" alignItems="center">
                  <Text fontSize="20px" color="red.500" fontWeight="bold" textAlign="center">
                    Erro ao calcular frete, verifique os dados e tente novamente.
                  </Text>
                </DrawerBody>
              )}

              <Grid gridTemplateColumns="repeat(4, 1fr)" mt={6} gap={3} mx="auto" w="100%" maxW="1316px">
                <Flex
                  alignItens="center"
                  gap={2}
                  justifyContent="center"
                  border="1px solid #378BBF"
                  py="12px"
                  px="39px"
                  textTransform="uppercase"
                  fontSize="12px"
                >
                  Valor total dos Itens: <Text as="span">{formatPrice(valorTotalItens)}</Text>
                </Flex>
                <Flex
                  alignItens="center"
                  gap={2}
                  justifyContent="center"
                  border="1px solid #378BBF"
                  py="12px"
                  px="39px"
                  textTransform="uppercase"
                  fontSize="12px"
                >
                  Substituição tributária:{' '}
                  <Text as="span" color="red.500">
                    {formatPrice(parseFloat(infoProposta?.vlr_subs))}
                  </Text>
                </Flex>
                <Flex
                  alignItens="center"
                  gap={2}
                  justifyContent="center"
                  border="1px solid #378BBF"
                  py="12px"
                  px="39px"
                  textTransform="uppercase"
                  fontSize="12px"
                >
                  Valor do Frete: <Text as="span">{informacoesFrete.valor_total_frete_30}</Text>
                </Flex>
                <Flex
                  gap={2}
                  alignItens="center"
                  justifyContent="center"
                  border="1px solid #378BBF"
                  bg="#378BBF"
                  color="white"
                  py="12px"
                  px="39px"
                  textTransform="uppercase"
                  fontSize="12px"
                >
                  Valor total:{' '}
                  <Text as="span" fontWeight="bold">
                    {formatPrice(informacoesFrete?.valor_total_frete_itens)}
                  </Text>
                </Flex>
              </Grid>
            </DrawerBody>
          )}

          <ModalFooter display="flex" justifyContent="center" alignItems="center" gap={3} borderTop="1px solid #707070">
            <Button onClick={onClose} maxW="181px" w="full" bgColor="white" border="1px solid #378BBF" color="#378BBF">
              Voltar
            </Button>

            <Button
              colorScheme="red"
              ml={3}
              maxW="181px"
              w="full"
              disabled={!selectedItem}
              onClick={handleCalcSave}
              isLoading={salvarCotacaoFreteLoading}
              loadingText="Salvando..."
            >
              Confirmar e salvar
            </Button>
          </ModalFooter>
        </DrawerContent>
      </Drawer>
    </>
  )
}
